import * as React from "react";
import { useEffect, useRef } from "react";
import styled from "@mui/material/styles/styled";
import { useTranslate } from "../../../hooks/useTranslate";
import { GenericButton } from "../../uiControllers/genericButton";
import { AllergySelectionForm } from "./allergySelectionForm";
import { DocQuestion } from "../DocQuestion";
import { ClosedSingleQuestionView } from "../closedSingleQuestionView";
import { usePatient } from "../../../state/patientContext";
import { SelectionType } from "../../uiControllers/AnswerButton";
import { SymptomText } from "../../uiControllers/SymptomText";
import { AnswerChip } from "../../../style/chips";
import { QuestionsIF } from "../QuestionsIF";
import { useBackgroundQuestionLogic } from "../hooks/useBackgroundQuestionLogic";
import { QuestionOptionsContainer } from "../layout/questionOptionsContainer";
import { PatientColorMap } from "../../../layout/patientColorMap";
import ClearIcon from "@mui/icons-material/Clear";
import { QuestionButtonArea } from "../ClosedMultipleQuestionView";
import { usePatientFeatureFlags } from "../../../hooks/usePatientFeatureFlags";
import { useClinicData } from "../../../clinic/clinicHooks";
//region [[ Styles ]]
const AllergyQuestionView = styled("div")({
  width: "100%",
});

const AllergyChipsContainer = styled("div")({
  display: "flex",
  flexFlow: "row",
  justifyContent: "flex-end",
  alignItems: "center",
  flexWrap: "wrap",
  width: "100%",
});

// TODO: use same css as medication
const AllergyChip = styled(AnswerChip)({
  margin: "10px",
  height: "50px",
  fontSize: "12px",
  flexBasis: "30.3%",
  minWidth: "170px", // Tested with "Alpha-adrenoceptor agonist"
  padding: 0,
  "& .patient-MuiChip-label": {
    whiteSpace: "normal",
  },
  "& .patient-MuiChip-icon": {
    color: PatientColorMap.White,
    margin: "5px",
  },
});
//endregion [[ Styles ]]
//region [[ Props ]]
export interface AllergyQuestionProps extends QuestionsIF {}
//endregion [[ Props ]]
//region [[ Functions ]]
//endregion [[ Functions ]]

export const AllergyQuestion = ({
  onAnswer,
  answer,
  ...props
}: AllergyQuestionProps) => {
  const { translate } = useTranslate();

  const { gender } = usePatient();

  const scrollRef = useRef(null);

  const { name: clinicName, clinic: clinicId } = useClinicData();

  const askBeforePresentingBackgroundQuestions =
    usePatientFeatureFlags("askBeforePresentingBackgroundQuestions") || true;

  const {
    answerSelectionType: hasAllergies,
    handleInitialAnswer,
    answers: allergies,
    onRemove,
    onAdd,
  } = useBackgroundQuestionLogic(
    onAnswer,
    answer,
    "allergyQuestion",
    clinicName,
    clinicId,
  );

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollIntoView({
        behavior: "smooth",
        block: "end",
        inline: "end",
      });
    }
  }, []);

  return (
    <AllergyQuestionView>
      <DocQuestion
        text={translate("ALLERGY_QUESTION")}
        miniView={props.miniView}
      />
      {askBeforePresentingBackgroundQuestions &&
        hasAllergies === null &&
        !answer && (
          <ClosedSingleQuestionView
            gender={gender}
            onClick={handleInitialAnswer}
            ref={scrollRef}
          />
        )}
      {(!askBeforePresentingBackgroundQuestions ||
        hasAllergies === SelectionType.YES) &&
        !answer && (
          <QuestionOptionsContainer>
            <AllergyChipsContainer>
              {allergies.map((qf, index) => (
                <AllergyChip
                  key={qf.id}
                  className={"answer-chip"}
                  label={<SymptomText symptom={qf} />}
                  onClick={() => onRemove(index)}
                  icon={<ClearIcon />}
                  data-selected
                />
              ))}
            </AllergyChipsContainer>
            <AllergySelectionForm
              onAllergyChange={onAdd}
              ref={scrollRef}
              isSecond={!!allergies?.length}
            />
            <QuestionButtonArea>
              <GenericButton
                className={"submit-button"}
                label={translate(allergies?.length ? "ANSWER_DONE" : "SKIP")}
                onClick={() => onAnswer(allergies)}
              />
            </QuestionButtonArea>
          </QuestionOptionsContainer>
        )}
    </AllergyQuestionView>
  );
};
